import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import logger from '@/lib/logger';

interface User {
  id: string;
  email: string;
  full_name: string | null;
  is_active: boolean;
  created_at: string;
  profiles?: {
    full_name: string | null;
    phone: string | null;
    department: string | null;
    position: string | null;
    employee_id: string | null;
    area: string | null;
    hire_date: string | null;
    last_login: string | null;
  };
  user_roles?: {
    role: string;
  }[];
}

interface ExportUsersButtonProps {
  users: User[] | undefined;
  isLoading?: boolean;
}

export function ExportUsersButton({ users, isLoading }: ExportUsersButtonProps) {
  const [exporting, setExporting] = useState(false);

  const formatDate = (date: string | null) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("es-ES");
  };

  const formatDateTime = (date: string | null) => {
    if (!date) return "";
    return new Date(date).toLocaleString("es-ES");
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      admin: "Administrador",
      supervisor: "Supervisor",
      operator: "Operador",
      quality: "Calidad",
      warehouse: "Almacén",
      maintenance: "Mantenimiento",
      viewer: "Visor",
    };
    return labels[role] || role;
  };

  const prepareData = () => {
    if (!users) return [];

    return users.map((user) => ({
      "ID Empleado": user.profiles?.employee_id || "",
      "Nombre Completo": user.profiles?.full_name || user.full_name || "",
      Email: user.email,
      Teléfono: user.profiles?.phone || "",
      Departamento: user.profiles?.department || "",
      Puesto: user.profiles?.position || "",
      Área: user.profiles?.area || "",
      Rol: getRoleLabel(user.user_roles?.[0]?.role || "viewer"),
      Estado: user.is_active ? "Activo" : "Inactivo",
      "Fecha de Alta": formatDate(user.profiles?.hire_date || null),
      "Último Acceso": formatDateTime(user.profiles?.last_login || null),
      "Fecha de Creación": formatDateTime(user.created_at),
    }));
  };

  const exportToExcel = async () => {
    if (!users || users.length === 0) {
      toast.error("No hay usuarios para exportar");
      return;
    }

    setExporting(true);
    try {
      const data = prepareData();
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      
      // Ajustar ancho de columnas
      const colWidths = [
        { wch: 12 }, // ID Empleado
        { wch: 25 }, // Nombre
        { wch: 30 }, // Email
        { wch: 15 }, // Teléfono
        { wch: 15 }, // Departamento
        { wch: 20 }, // Puesto
        { wch: 15 }, // Área
        { wch: 15 }, // Rol
        { wch: 10 }, // Estado
        { wch: 12 }, // Fecha Alta
        { wch: 18 }, // Último Acceso
        { wch: 18 }, // Fecha Creación
      ];
      worksheet["!cols"] = colWidths;

      XLSX.utils.book_append_sheet(workbook, worksheet, "Usuarios");
      
      const fileName = `usuarios_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
      toast.success(`${users.length} usuarios exportados correctamente`);
    } catch (error) {
      logger.error("Error exporting to Excel:", error);
      toast.error("Error al exportar a Excel");
    } finally {
      setExporting(false);
    }
  };

  const exportToCSV = async () => {
    if (!users || users.length === 0) {
      toast.error("No hay usuarios para exportar");
      return;
    }

    setExporting(true);
    try {
      const data = prepareData();
      const worksheet = XLSX.utils.json_to_sheet(data);
      const csv = XLSX.utils.sheet_to_csv(worksheet);
      
      // Crear blob y descargar
      const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `usuarios_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`${users.length} usuarios exportados correctamente`);
    } catch (error) {
      logger.error("Error exporting to CSV:", error);
      toast.error("Error al exportar a CSV");
    } finally {
      setExporting(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" disabled={isLoading || exporting || !users?.length}>
          {exporting ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          Exportar
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={exportToExcel}>
          <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
          Exportar a Excel (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportToCSV}>
          <FileText className="w-4 h-4 mr-2 text-blue-600" />
          Exportar a CSV (.csv)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}





